import moment from 'moment';
import _each from 'lodash/each';
import _isNil from 'lodash/isNil';
import _toNumber from 'lodash/toNumber';
import { Dictionary } from 'lodash';
import { Driver, OPTIMIZATION_TYPE, Task, WayPoint } from '@bringg/types';
import { RoutesPlannerBreak, RoutesPlannerTask } from '@bringg-frontend/routes-planner';
import { EtaResponseRouteItem } from '@bringg-frontend/eta-calculator';
import { getDropOffWayPoint } from '@bringg-frontend/utils';

import { ManualOptimizationItem, OptimizationItemType } from './models';

const convertTimeToUnixSeconds = (date: string): number => {
	return moment(date).unix();
};

const NO_ROUTE_ID = -1;

export const prepareDataForOptimization = (
	runToTasks: Dictionary<RoutesPlannerTask[]>,
	runToBreaks: Dictionary<RoutesPlannerBreak[]>,
	runToDriver: Dictionary<Driver>,
	routeInfoByIndex: Dictionary<EtaResponseRouteItem>,
	originalRouteInfoByIndex: Dictionary<EtaResponseRouteItem>,
	optimizationType: OPTIMIZATION_TYPE,
	isManual: boolean
): ManualOptimizationItem[] => {
	const considerOnlyDirtyTasks = isManual;
	const result: ManualOptimizationItem[] = [];
	_each(runToTasks, (tasks, run_id) => {
		const runId: number = parseInt(run_id, 10);
		let routeInfo;

		if (tasks && tasks[0]) {
			if (routeInfoByIndex[runId]) {
				routeInfo = routeInfoByIndex[runId] as ManualOptimizationItem<OptimizationItemType.ROUTE_INFO>;
			} else if (originalRouteInfoByIndex[runId]) {
				routeInfo = originalRouteInfoByIndex[runId] as ManualOptimizationItem<OptimizationItemType.ROUTE_INFO>;
			} else {
				routeInfo = Object.values(originalRouteInfoByIndex).find(
					originalRouteInfo => originalRouteInfo.run_id === runId
				);
			}
		}

		if (routeInfo) {
			result.push(routeInfo);
		}
		const relevantTasks = considerOnlyDirtyTasks ? tasks.filter(task => task.isDirty) : tasks;
		_each(relevantTasks, (task: Task) => {
			const wp = getDropOffWayPoint(task);
			const eta = convertTimeToUnixSeconds(wp.scheduled_at);
			const etl = wp.etl ? convertTimeToUnixSeconds(wp.etl) : eta;
			const userId = runToDriver[runId]?.id;

			let optimizationItem;
			if (runId === NO_ROUTE_ID) {
				optimizationItem = createOptimizationUnassignedTask(task, wp, eta, etl);
			} else {
				optimizationItem = createOptimizationTask(
					task,
					wp,
					eta,
					etl,
					optimizationType,
					runId,
					userId,
					routeInfo,
					isManual
				);
			}
			result.push(optimizationItem);
		});

		const currentBreaks = runToBreaks[run_id];
		if (currentBreaks?.length) {
			const breakItems: ManualOptimizationItem<OptimizationItemType.BREAK>[] = currentBreaks.map(
				brk =>
					({
						type: OptimizationItemType.BREAK,
						...brk,
						scheduled_start_time: convertTimeToUnixSeconds(brk.scheduled_start_time),
						scheduled_end_time: convertTimeToUnixSeconds(brk.scheduled_end_time),
						estimated_start_time: convertTimeToUnixSeconds(brk.estimated_start_time),
						estimated_end_time: convertTimeToUnixSeconds(brk.estimated_end_time)
					} as ManualOptimizationItem<OptimizationItemType.BREAK>)
			);

			result.push(...breakItems);
		}
	});

	return result;
};

const createOptimizationUnassignedTask = (
	task: Task,
	wp: WayPoint,
	eta: number,
	etl: number
): ManualOptimizationItem<OptimizationItemType.UNASSIGNED_TASK> => {
	return {
		type: OptimizationItemType.UNASSIGNED_TASK,
		id: task.id,
		priority: task.priority,
		way_point_id: wp.id,
		eta,
		etl
	};
};

const createOptimizationTask = (
	task: Task,
	wp: WayPoint,
	eta: number,
	etl: number,
	optimizationType: OPTIMIZATION_TYPE,
	runId: number,
	userId: number,
	routeInfo: EtaResponseRouteItem,
	isManual: boolean
): ManualOptimizationItem<OptimizationItemType.TASK> => {
	const optimizationItem: ManualOptimizationItem<OptimizationItemType.TASK | OptimizationItemType.UNASSIGNED_TASK> = {
		type: OptimizationItemType.TASK,
		task_id: task.id,
		priority: task.priority,
		way_point_id: wp.id,
		eta,
		etl,
		user_id: userId,
		vehicle_id: task.vehicle_id
	};

	if (task.priority === 1 && wp.has_to_leave_by) {
		optimizationItem.depot_etl = convertTimeToUnixSeconds(wp.has_to_leave_by);
		optimizationItem.has_to_leave_by = optimizationItem.depot_etl;
	}

	if (!_isNil(task.user?.user_type_id)) {
		optimizationItem.user_type_id = task.user.user_type_id;
	}

	// @ts-ignore
	if (!_isNil(task.driver_idx)) {
		// @ts-ignore
		optimizationItem.driver_idx = _toNumber(task.driver_idx);
	}

	if (routeInfo?.run_id !== routeInfo?.route_idx) {
		optimizationItem.route_idx = routeInfo.route_idx;
		optimizationItem.run_id = routeInfo.run_id;
	} else if (!_isNil(runId)) {
		optimizationItem.route_idx = runId;

		if (isManual) {
			optimizationItem.run_id = runId;
		}
	}

	return optimizationItem;
};
